import * as i1 from '@ngrx/store';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, select, StoreModule } from '@ngrx/store';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, APP_INITIALIZER, NgModule, Optional } from '@angular/core';
import { combineLatest, throwError, of } from 'rxjs';
import { mergeMap, take, map, first, catchError, timeout, switchMap, filter, withLatestFrom, concatMap } from 'rxjs/operators';
import { createEntityAdapter } from '@ngrx/entity';
import * as i2 from '@panamax/app-state';
import * as i1$2 from '@ngrx/effects';
import { createEffect, ofType, concatLatestFrom, EffectsModule } from '@ngrx/effects';
import * as i1$1 from '@angular/common/http';
import { HttpParams } from '@angular/common/http';
import dayjs from 'dayjs';
import { PriceEventActions } from '@usf/price-types';
import { SellerPriceDetailsEventActions } from '@usf/price-types/price';
import * as i1$3 from '@panamax/websocket';

var ProcessingState;
(function (ProcessingState) {
    ProcessingState["error"] = "error";
    ProcessingState["processing"] = "processing";
    ProcessingState["success"] = "success";
})(ProcessingState || (ProcessingState = {}));
var ManualUpdateReason;
(function (ManualUpdateReason) {
    ManualUpdateReason["keepCompetitionOut"] = "Keep Competition Out";
    ManualUpdateReason["newLinesForThisCustomer"] = "New Lines For This Customer";
    ManualUpdateReason["qmi"] = "QMI - Out Of Market Pricing";
    ManualUpdateReason["other"] = "Other";
})(ManualUpdateReason || (ManualUpdateReason = {}));

/*eslint no-shadow: 0*/
var LoadingState;
(function (LoadingState) {
    LoadingState["init"] = "init";
    LoadingState["loading"] = "loading";
    LoadingState["loaded"] = "loaded";
    LoadingState["notFound"] = "notFound";
    LoadingState["error"] = "error";
    LoadingState["reloading"] = "reloading";
})(LoadingState || (LoadingState = {}));

const PRODUCT_PRICING_ACTIONS = {
    resetStore: createAction('[NgRxPricing - Product Pricing] Reset Product Pricing'),
    loadProductPricingFromCache: createAction('[NgRxPricing - Product Pricing] Load Product Pricing From Cache'),
    loadProductPricingFromCacheSuccess: createAction('[NgRxPricing - Product Pricing] Lists Loaded From Cache Successfully', props()),
    loadProductPricing: createAction('[NgRxPricing - Product Pricing] Load Product Pricing From Domain Api', props()),
    fetchProductPricing: createAction('[NgRxPricing - Product Pricing] Fetching Product Pricing From Domain Api', props()),
    fetchProductPricingBatch: createAction('[NgRxPricing - Product Pricing] Fetching Product Pricing Batch From Domain Api', props()),
    fetchProductPricingSuccess: createAction('[NgRxPricing - Product Pricing] Fetching Prices Loaded From Domain Successfully', props()),
    fetchProductPricingFail: createAction('[NgRxPricing - Product Pricing] Fetching Product Pricing From Domain Api Failed', props()),
    writeProductPricingToOfflineCache: createAction('[NgRxPricing - Product Pricing] Write Product Pricing To Offline Cache', props()),
    loadProductPricingAsync: createAction('[NgRxPricing - Product Pricing Resolver Async] Load Product Pricing Async', props()),
    productPriceCreated: createAction('[NgRxPricing - Product Pricing Created/WebSocket Event] Create Product Pricing', props()),
    productPricingUpdated: createAction('[NgRxPricing - Product Pricing Updated/WebSocket Event] Update Product Pricing', props()),
    productPricingDeleted: createAction('[NgRxPricing - Product Pricing Deleted/WebSocket Event] Delete Product Pricing', props()),
};

const SELLER_PRICING_ACTIONS = {
    resetStore: createAction('[NgRxPricing - Seller Pricing] Reset Seller Pricing'),
    loadSellerPricing: createAction('[NgRxPricing -  Seller Pricing] Load  Seller Pricing From Domain Api', props()),
    loadSellerPricingSuccess: createAction('[NgRxPricing -  Seller Pricing]  Seller Prices Loaded From Domain Successfully', props()),
    loadSellerPricingFail: createAction('[NgRxPricing -  Seller Pricing] Load  Seller Pricing From Domain Api Failed', props()),
    sellerPriceCreated: createAction('[NgRxPricing - Seller Pricing Created/WebSocket Event] Create Seller Pricing', props()),
    sellerPricingUpdated: createAction('[NgRxPricing - Seller Pricing Updated/WebSocket Event] Update Seller Pricing', props()),
    sellerPricingDeleted: createAction('[NgRxPricing - Seller Pricing Deleted/WebSocket Event] Delete Seller Pricing', props()),
};

const CPR_PRICING_ACTIONS = {
    resetStore: createAction('[NgRxPricing - CPR Pricing] Reset CPR Pricing'),
    updateCPRPricing: createAction('[NgRxPricing - CPR Pricing] Updating CPR Pricing From Domain Api', props()),
    updateCPRPricingSuccess: createAction('[NgRxPricing - CPR Pricing] Updating CPR Pricing From Domain Successfully', props()),
    updateCPRPricingFail: createAction('[NgRxPricing - CPR Pricing] Updating CPR Pricing From Domain Api Failed', props()),
};

const EASY_PRICING_ACTIONS = {
    resetStore: createAction('[NgRxPricing - Easy Pricing] Reset Easy Pricing'),
    loadEasyPricing: createAction('[NgRxPricing - Easy Pricing] Load Easy Pricing', props()),
    fetchEasyPricing: createAction('[NgRxPricing - Easy Pricing] Fetch Easy Pricing From Domain Api', props()),
    fetchEasyPricingSuccess: createAction('[NgRxPricing - Easy Pricing] Easy Pricing Loaded From Domain Successfully', props()),
    fetchEasyPricingFail: createAction('[NgRxPricing - Easy Pricing] Load Easy Pricing From Domain Api Failed', props()),
    updateEasyPricing: createAction('[NgRxPricing - Easy Pricing] Update Easy Pricing', props()),
    updateEasyPricingSuccess: createAction('[NgRxPricing - Easy Pricing] Update Easy Pricing Success', props()),
    updateEasyPricingFail: createAction('[NgRxPricing - Easy Pricing] Update Easy Pricing Fail', props()),
    resetProcessingStates: createAction('[Easy Pricing] Reset Processing States'),
    resetProcessingStateForProduct: createAction('[Easy Pricing] Reset Processing State For Product', props()),
};

const PRICING_KEY = 'pricing';
const SELLER_PRICING_KEY = 'seller-pricing';
const CPR_PRICING_KEY = 'cpr-pricing';
const EASY_PRICING_KEY = 'easy-pricing';

const FALL_BACK_PRICING_LIST = ['2J', '2K', '2L', '2M'];

/* eslint-disable no-shadow */
const productPricingAdapter = createEntityAdapter({
    selectId: productPrice => productPrice.productNumber,
});
const initiaProductPricingState = productPricingAdapter.getInitialState({
    // add additional entity state properties here if needed
    loadingState: LoadingState.init,
    error: null,
    watermark: '',
    effectiveDate: null,
    effectiveTime: null,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _productPricingtReducer = createReducer(initiaProductPricingState, 
// Reset store
on(PRODUCT_PRICING_ACTIONS.resetStore, () => initiaProductPricingState), 
// Set this elements to be on a 'loading' state
on(PRODUCT_PRICING_ACTIONS.fetchProductPricing, (state, { productNumbers }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.loading,
    }));
    return productPricingAdapter.upsertMany(updateChanges, state);
}), on(PRODUCT_PRICING_ACTIONS.fetchProductPricingSuccess, (state, { requestedProductNumbers, productPricing, effectiveDate, effectiveTime }) => {
    const notRetrievedProductPricingChanges = [];
    const retrievedSet = new Set();
    const watermark = new Date().toISOString();
    const retrievedProductPricingChanges = productPricing?.map(pricing => {
        retrievedSet.add(Number(pricing.productNumber));
        return {
            ...Object.assign({}, pricing, {
                watermark,
            }),
            loadingState: LoadingState.loaded,
            noCache: FALL_BACK_PRICING_LIST.includes(pricing.priceSource),
        };
    });
    requestedProductNumbers.forEach(prodNumber => {
        if (!retrievedSet.has(prodNumber)) {
            notRetrievedProductPricingChanges.push({
                productNumber: prodNumber,
                loadingState: LoadingState.notFound,
            });
        }
    });
    const allChanges = [
        ...notRetrievedProductPricingChanges,
        ...retrievedProductPricingChanges,
    ];
    return productPricingAdapter.upsertMany(allChanges, {
        ...state,
        loading: false,
        loaded: true,
        effectiveDate,
        effectiveTime,
        watermark: new Date().toISOString(),
    });
}), on(PRODUCT_PRICING_ACTIONS.fetchProductPricingFail, (state, { productNumbers, error }) => {
    const updateChanges = productNumbers.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.error,
    }));
    return productPricingAdapter.upsertMany(updateChanges, state);
}));
const cacheActions = {
    [`${PRODUCT_PRICING_ACTIONS.fetchProductPricingSuccess}`]: true,
};
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function productPricingReducer(state, action) {
    // persist list state here
    // determine if action is a list action
    const result = _productPricingtReducer(state, action);
    if (cacheActions[action.type]) {
        // possible hook into writing to offline cache
    }
    return result;
}

const selectProductPricingState = createFeatureSelector(PRICING_KEY);
const productPricingLoaded = () => createSelector(selectProductPricingState, productPrices => productPrices.loadingState === LoadingState.loaded);
const allProductPricing = () => createSelector(selectProductPricingState, (productPrices) => Object.values(productPrices));
const priceByProductNumber = () => createSelector(selectProductPricingState, (productPricing, props) => productPricing[props.productNumber]);
const selectPendingProductPrices = (productNumbers) => createSelector(selectAllProductPricing$1, productPricings => {
    const result = productNumbers.filter(productNumber => {
        // product number should not be undefined
        if (!productNumber) {
            return false;
        }
        const productPricing = productPricings.find(pricing => +pricing.productNumber === productNumber);
        if (!productPricing) {
            // Element wasn't found in the state, so we need to fetch it
            return true;
        }
        // Or element is in hasn't been fetch or previous attempt encountered an error
        return productPricing.loadingState === LoadingState.init;
    });
    return result;
});
const isProductPricingLoaded = (productNumbers) => createSelector(selectProductPricingEntities$1, (pricingEntities) => productNumbers.every(productNumber => {
    const productPricing = pricingEntities[productNumber];
    return (productPricing &&
        (productPricing.loadingState === LoadingState.loaded ||
            productPricing.loadingState === LoadingState.error ||
            productPricing.loadingState === LoadingState.notFound));
}));
const { selectIds: selectProductPricingIds$1, selectEntities: selectProductPricingEntities$1, selectAll: selectAllProductPricing$1, selectTotal: selectTotalProductPricing$1, } = productPricingAdapter.getSelectors(selectProductPricingState);

class ProductPricingService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
        this.productPricingState$ = () => this.store.select(selectProductPricingState);
        this.productPricing$ = () => this.store.select(allProductPricing);
        this.getPriceByProductNumber$ = (productNumber) => this.store.select(priceByProductNumber, { productNumber });
    }
    getPrice(productNumber) {
        return this.getPrices([productNumber]).pipe(mergeMap(p => p));
    }
    loadPrices(productNumbers) {
        combineLatest([this.panAppState.customer$, this.panAppState.session$])
            .pipe(take(1))
            .subscribe(([cust, session]) => {
            if (!(cust?.ogPrintPriceInd === 'N' || session?.isGuest)) {
                this.store.dispatch(PRODUCT_PRICING_ACTIONS.loadProductPricing({
                    productNumbers,
                }));
            }
        });
    }
    getPrices(productNumbers) {
        this.loadPrices(productNumbers);
        return this.store.select(selectProductPricingEntities$1).pipe(map(d => Object.keys(d)
            .filter(k => productNumbers.includes(+k))
            .map(k => d[k])));
    }
}
ProductPricingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductPricingService, deps: [{ token: i1.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
ProductPricingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductPricingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductPricingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i2.PanAppState }]; } });

const sellerPricingAdapter = createEntityAdapter({
    selectId: productPrice => productPrice.productNumber,
});
const initialSellerPricingState = sellerPricingAdapter.getInitialState({
    loaded: false,
    loading: false,
    error: null,
    watermark: '',
    effectiveDate: null,
    effectiveTime: null,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _sellerPricingReducer = createReducer(initialSellerPricingState, 
// Reset store
on(SELLER_PRICING_ACTIONS.resetStore, () => initialSellerPricingState), on(SELLER_PRICING_ACTIONS.loadSellerPricingSuccess, (state, action) => {
    console.log(action);
    const sellerPriceDetails = action?.sellerPriceDetails.map(sellerPrice => {
        if (sellerPrice !== null) {
            return {
                ...sellerPrice,
                customerAscInd: action.customerAscInd,
                districtNgpCprInd: action.districtNgpCprInd,
                loading: false,
                loaded: true,
            };
        }
    });
    return sellerPricingAdapter.upsertMany(sellerPriceDetails, {
        ...state,
        loading: false,
        loaded: true,
        effectiveDate: action.effectiveDate,
        effectiveTime: action.effectiveTime,
        watermark: new Date().toISOString(),
    });
}));
const sellerPricingCacheActions = {
    [`${SELLER_PRICING_ACTIONS.loadSellerPricingSuccess}`]: true,
};
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function sellerPricingReducer(state, action) {
    // persist list state here
    // determine if action is a list action
    const result = _sellerPricingReducer(state, action);
    if (sellerPricingCacheActions[action.type]) {
    }
    return result;
}

const { selectAll: selectAll$1 } = sellerPricingAdapter.getSelectors();
const selectSellerPricingState = createFeatureSelector(SELLER_PRICING_KEY);
const sellerPricingLoaded = () => createSelector(selectSellerPricingState, sellerPrices => sellerPrices.loaded);
const allSellerPricing = () => createSelector(selectSellerPricingState, (sellerPrices) => Object.values(sellerPrices));
const sellerPriceByProductNumber = () => createSelector(selectSellerPricingState, (sellerPricing, props) => sellerPricing[props.productNumber]);
const selectAllSellerPrice = createSelector(selectSellerPricingState, selectAll$1);
const { selectIds: selectProductPricingIds, selectEntities: selectProductPricingEntities, selectAll: selectAllProductPricing, selectTotal: selectTotalProductPricing, } = sellerPricingAdapter.getSelectors(selectSellerPricingState);

const TRACE_CONTEXT = {
    price: 'price',
    sellerPrice: 'sellerPrice',
    cpr: 'cpr',
};
const TRACE_EVENT = {
    getPrices: 'get prices',
    getSellerPrices: 'get seller prices',
    cpr: 'cpr',
};

const initialPriceTrace = (productNumbers, synchronous) => ({
    tracing: {
        data: {
            traceContext: TRACE_CONTEXT.price,
            isStartOfTrace: true,
            isEndOfTrace: false,
            attributes: {
                productNumbers: productNumbers?.length,
                synchronousPricing: synchronous,
            },
        },
        transformFunc: tracingData => ({
            event: TRACE_EVENT.getPrices,
            request: {
                productNumbers: tracingData.data.attributes?.productNumbers,
                synchronous: tracingData.data.attributes?.synchronousPricing,
            },
        }),
    },
});
const finalPriceTrace = (success, synchronous) => ({
    tracing: {
        data: {
            traceContext: TRACE_CONTEXT.price,
            isStartOfTrace: false,
            isEndOfTrace: true,
            attributes: {
                success,
                synchronousPricing: synchronous,
            },
        },
        transformFunc: tracingData => ({
            request: {
                success: tracingData.data.attributes?.success,
            },
        }),
    },
});
const initialSellerPriceTrace = (productNumbers) => ({
    tracing: {
        data: {
            traceContext: TRACE_CONTEXT.sellerPrice,
            isStartOfTrace: true,
            isEndOfTrace: false,
            attributes: {
                productNumbers: productNumbers?.length,
            },
        },
        transformFunc: tracingData => ({
            event: TRACE_EVENT.getSellerPrices,
            request: {
                productNumbers: tracingData.data.attributes?.productNumbers,
            },
        }),
    },
});
const finalSellerPriceTrace = (success) => ({
    tracing: {
        data: {
            traceContext: TRACE_CONTEXT.sellerPrice,
            isStartOfTrace: false,
            isEndOfTrace: true,
            attributes: {
                success,
            },
        },
        transformFunc: tracingData => ({
            request: {
                success: tracingData.data.attributes?.success,
            },
        }),
    },
});
const priceTrackingEvents = {
    initialPriceTrace,
    finalPriceTrace,
};
const sellerPriceTrackingEvents = {
    initialSellerPriceTrace,
    finalSellerPriceTrace,
};

const getCurrentPathname = () => window.location.pathname;

class SelectPricingService {
    constructor(store) {
        this.store = store;
        this.sellerPricingState$ = () => this.store.select(selectSellerPricingState);
        this.sellerPricings$ = () => this.store.select(allSellerPricing);
        this.getSellerPriceByProductNumber$ = (productNumber) => this.store.select(sellerPriceByProductNumber, { productNumber });
    }
    getSellerPrice(productNumber) {
        return this.getSellerPrices([productNumber]).pipe(map(sellerPrices => sellerPrices.filter(sellerPrice => sellerPrice.productNumber === productNumber)[0]));
    }
    getSellerPrices(productNumbers) {
        this.store.dispatch(SELLER_PRICING_ACTIONS.loadSellerPricing({
            productNumbers,
            feature: getCurrentPathname(),
            tracking: sellerPriceTrackingEvents.initialSellerPriceTrace(productNumbers),
        }));
        return this.store.pipe(select(selectAllSellerPrice));
    }
}
SelectPricingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectPricingService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
SelectPricingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectPricingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SelectPricingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const cprPricingAdapter = createEntityAdapter({
    selectId: searchResponse => 0,
});
const initialCprPricingState = cprPricingAdapter.getInitialState({
    loaded: false,
    loading: false,
    error: null,
});
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _cprPricingReducer = createReducer(initialCprPricingState, on(CPR_PRICING_ACTIONS.resetStore, () => initialCprPricingState), on(CPR_PRICING_ACTIONS.updateCPRPricing, (state, { productNumber, targetPrice, requestType, districtNgpCprInd, productUOM, priceEffectiveDate, requestManualApproval, }) => {
    const updateChange = {
        productNumber,
        targetPrice,
        requestType,
        districtNgpCprInd,
        productUOM,
        priceEffectiveDate,
        requestManualApproval,
        responseCode: 0,
        loadingState: LoadingState.loading,
    };
    return cprPricingAdapter.upsertOne(updateChange, state);
}), on(CPR_PRICING_ACTIONS.updateCPRPricingSuccess, (state, { result, unifiedResponseCode }) => {
    const currentRequest = state.entities[0];
    const updateChange = {
        ...currentRequest,
        loadingState: LoadingState.loaded,
        ...result,
        responseCode: result.responseCode,
        unifiedResponseCode,
    };
    return cprPricingAdapter.upsertOne(updateChange, state);
}), on(CPR_PRICING_ACTIONS.updateCPRPricingFail, (state, { result, unifiedResponseCode }) => {
    const currentRequest = state.entities[0];
    const updateChange = {
        ...currentRequest,
        loadingState: LoadingState.error,
        responseCode: result.responseCode,
        ...result,
        unifiedResponseCode,
    };
    return cprPricingAdapter.upsertOne(updateChange, state);
}));
const cprPricingCacheActions = {
    [`${CPR_PRICING_ACTIONS.updateCPRPricingSuccess}`]: true,
};
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function cprPricingReducer(state, action) {
    return _cprPricingReducer(state, action);
}

const { selectAll } = cprPricingAdapter.getSelectors();
const selectCprPricingState = createFeatureSelector(CPR_PRICING_KEY);
const cprPricingLoaded = () => createSelector(selectCprPricingState, cprPrice => cprPrice.loadingState === LoadingState.loaded);
const selectCprResponse = state => cprPricingAdapter.getSelectors(selectCprPricingState).selectAll(state)[0];
const selectAllCprPrice = createSelector(selectCprPricingState, selectAll);
const { selectIds: selectCprPricingIds, selectEntities: selectCprPricingEntities, selectAll: selectAllCprPricing, selectTotal: selectTotalCprPricing, } = cprPricingAdapter.getSelectors(selectCprPricingState);

const genericTracingTransformer = (trackingData) => {
    const { traceContext, isStartOfTrace, isEndOfTrace, ...rest } = trackingData.data;
    return {
        traceContext,
        isStartOfTrace,
        isEndOfTrace,
        ...rest.attributes,
    };
};

const populateTraceHeader = (action) => action.tracking?.tracing?.data?.traceContext;
const startTrace = (traceContext, productNumber, targetPrice, extraAttributes) => ({
    tracing: {
        data: {
            traceContext,
            isStartOfTrace: true,
            isEndOfTrace: false,
            attributes: {
                productNumber,
                targetPrice,
                ...(extraAttributes || {}),
            },
        },
        transformFunc: genericTracingTransformer,
    },
});
const endTrace = (traceContext, productNumber, targetPrice, extraAttributes) => ({
    tracing: {
        data: {
            traceContext,
            isStartOfTrace: false,
            isEndOfTrace: true,
            attributes: {
                productNumber,
                targetPrice,
                ...(extraAttributes || {}),
            },
        },
        transformFunc: genericTracingTransformer,
    },
});
/**
 * Creates a success tracking object for CPR price update operations
 */
const createCprSuccessTrackingObject = (tracking, responseCode, responseMessage, productNumber) => ({
    tracing: {
        data: {
            traceContext: tracking.tracing.data.traceContext,
            isEndOfTrace: true,
            isStartOfTrace: false,
            attributes: {
                ...tracking.tracing.data.attributes,
                responseCode,
                responseMessage,
                productNumber,
                success: true,
            },
        },
        transformFunc: tracking.tracing.transformFunc,
    },
});
/**
 * Creates a failure tracking object for CPR price update operations
 */
const createCprFailureTrackingObject = (tracking, error, productNumber, responseCode) => ({
    tracing: {
        data: {
            traceContext: tracking.tracing.data.traceContext,
            isEndOfTrace: true,
            isStartOfTrace: false,
            attributes: {
                ...tracking.tracing.data.attributes,
                responseCode: responseCode || error?.status || 500,
                responseMessage: error.message,
                productNumber,
                success: false,
                error: {
                    status: error?.status || 500,
                    message: error.message,
                },
            },
        },
        transformFunc: tracking.tracing.transformFunc,
    },
});
/**
 * Creates a start tracking object for auto approval CPR operations
 */
const createAutoApprovalStartTrackingObject = (traceContext, productNumber, requestedPrice, priceType) => ({
    tracing: {
        data: {
            traceContext,
            isStartOfTrace: true,
            isEndOfTrace: false,
            attributes: {
                productNumber,
                requestedPrice,
                priceType,
                approvalType: 'auto',
            },
        },
        transformFunc: genericTracingTransformer,
    },
});
/**
 * Creates a start tracking object for manual approval CPR operations
 */
const createManualApprovalStartTrackingObject = (traceContext, productNumber, requestedPrice, reason) => ({
    tracing: {
        data: {
            traceContext,
            isStartOfTrace: true,
            isEndOfTrace: false,
            attributes: {
                productNumber,
                requestedPrice,
                reason,
                approvalType: 'manual',
            },
        },
        transformFunc: genericTracingTransformer,
    },
});
const traceHelperUtil = {
    populateTraceHeader,
    startTrace,
    endTrace,
    createCprSuccessTrackingObject,
    createCprFailureTrackingObject,
    createAutoApprovalStartTrackingObject,
    createManualApprovalStartTrackingObject,
};

class CprPricingService {
    constructor(store) {
        this.store = store;
    }
    /**
     *
     * @param productNumber the product number
     * @param targetPrice the price that the product should have after the change
     * @param requestType type of CPR request
     * @param districtNgpCprInd 'N' / 'Y' indicator of legacy/NGP experience
     * @param productUOM product UOM string. Example: 'CS'
     * @param requestManualApproval use it for manual request in NGP experience. False by default.
     * @param reason use it for manual request in NGP experience. Example: 'Keep Competition Out'.
     * @param priceEffectiveDate Date is expected to be formatted like '2023-03-28T14:21:08.558Z'.
     * The format follows ISO8601 but date has to be localized,
     * and it's not an actual UTC date
     */
    updateCpr(productNumber, targetPrice, requestType, districtNgpCprInd, productUOM = 'CS', requestManualApproval = false, tandemSystem = '', reason, priceEffectiveDate = null) {
        this.store.dispatch(CPR_PRICING_ACTIONS.updateCPRPricing({
            productNumber,
            targetPrice,
            requestType,
            districtNgpCprInd,
            productUOM,
            priceEffectiveDate: priceEffectiveDate ?? this.getFormattedPriceEffectiveDate(new Date()),
            requestManualApproval,
            reason,
            tandemSystem,
            tracking: traceHelperUtil.startTrace(TRACE_CONTEXT.cpr, productNumber, targetPrice),
        }));
        return this.store.select(selectCprResponse).pipe(first(opportunity => opportunity.loadingState === LoadingState.loaded ||
            opportunity.loadingState === LoadingState.error), map(response => response));
    }
    /**
     * Produces a  localized date using the expected format for NGP
     * Date is expected to be formatted like '2023-03-28T14:21:08.558Z'.
     * The format follows ISO8601 but date has to be localized,
     * and it's not an actual UTC date
     */
    getFormattedPriceEffectiveDate(date) {
        const offsetMs = date.getTimezoneOffset() * 60 * 1000;
        const msLocal = date.getTime() - offsetMs;
        const dateLocal = new Date(msLocal);
        return dateLocal.toISOString();
    }
}
CprPricingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CprPricingService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
CprPricingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CprPricingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CprPricingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

const easyPricingAdapter = createEntityAdapter({
    selectId: easyPriceState => easyPriceState.productNumber,
});
const initialEasyPricingState = easyPricingAdapter.getInitialState();
// eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
const _easyPricingReducer = createReducer(initialEasyPricingState, on(EASY_PRICING_ACTIONS.resetStore, () => initialEasyPricingState), on(EASY_PRICING_ACTIONS.fetchEasyPricing, (state, action) => {
    const updateChanges = action.productNumbers.map(productNumber => {
        const existingEasyPricing = state.entities[productNumber];
        if (existingEasyPricing && !action.isFromFridayToggle) {
            return {
                ...existingEasyPricing,
                loadingState: LoadingState.reloading,
            };
        }
        return {
            productNumber,
            loadingState: LoadingState.loading,
            isFridayPricing: action.fridayPricing ?? false,
            processingState: undefined,
        };
    });
    return easyPricingAdapter.upsertMany(updateChanges, state);
}), on(EASY_PRICING_ACTIONS.fetchEasyPricingSuccess, (state, action) => {
    const watermark = new Date().toISOString();
    const retrievedSet = new Set();
    const easyPricingDetails = action.productList.map(sellerPricingPmeProductItem => {
        retrievedSet.add(sellerPricingPmeProductItem.productNumber);
        return {
            ...sellerPricingPmeProductItem,
            watermark,
            loadingState: LoadingState.loaded,
            isFridayPricing: action.isFridayPricing,
            manualCpr: 'manualCpr' in sellerPricingPmeProductItem
                ? sellerPricingPmeProductItem.manualCpr
                : undefined,
        };
    });
    const notRetrievedEasyPricingDetails = action.requestedProductNumbers
        .filter(productNumber => !retrievedSet.has(productNumber))
        .map(productNumber => ({
        productNumber,
        loadingState: LoadingState.notFound,
        isFridayPricing: action.isFridayPricing,
    }));
    return easyPricingAdapter.upsertMany([...notRetrievedEasyPricingDetails, ...easyPricingDetails], state);
}), on(EASY_PRICING_ACTIONS.fetchEasyPricingFail, (state, action) => {
    const failedEasyPricingDetails = action.productNumbers.map(productNumber => ({
        productNumber,
        loadingState: LoadingState.error,
        isFridayPricing: action.isFridayPricing,
    }));
    return easyPricingAdapter.upsertMany(failedEasyPricingDetails, state);
}), on(EASY_PRICING_ACTIONS.updateEasyPricing, (state, action) => {
    const existingEasyPricing = state.entities[action.easyPricingUpdate.productNumber];
    if (existingEasyPricing) {
        return easyPricingAdapter.upsertOne({
            ...existingEasyPricing,
            processingState: ProcessingState.processing,
            isCustomUpdate: action.easyPricingUpdate.isCustomUpdate,
            isManualUpdate: action.easyPricingUpdate.isManualUpdate,
            customPrice: action.easyPricingUpdate.isCustomUpdate
                ? action.easyPricingUpdate.price
                : undefined,
            unifiedResponseCode: 0,
        }, state);
    }
    return state;
}), on(EASY_PRICING_ACTIONS.updateEasyPricingSuccess, (state, action) => {
    const existingEasyPricing = state.entities[action.easyPricingUpdate.productNumber];
    if (existingEasyPricing) {
        return easyPricingAdapter.upsertOne({
            ...existingEasyPricing,
            processingState: ProcessingState.success,
            unifiedResponseCode: action.unifiedResponseCode,
            customPrice: action.unifiedResponseCode === 300
                ? action.easyPricingUpdate.price
                : undefined,
            manualCpr: action.easyPricingUpdate.isCustomUpdate &&
                action.easyPricingUpdate.isManualUpdate
                ? existingEasyPricing.manualCpr
                : undefined,
        }, state);
    }
    return state;
}), on(EASY_PRICING_ACTIONS.updateEasyPricingFail, (state, action) => {
    const existingEasyPricing = state.entities[action.easyPricingUpdate.productNumber];
    if (existingEasyPricing) {
        return easyPricingAdapter.upsertOne({
            ...existingEasyPricing,
            processingState: ProcessingState.error,
            unifiedResponseCode: action.unifiedResponseCode,
            customPrice: undefined,
        }, state);
    }
    return state;
}), on(EASY_PRICING_ACTIONS.resetProcessingStates, state => {
    const updates = Object.keys(state.entities).map(key => ({
        ...state.entities[key],
        processingState: undefined,
        isManualUpdate: undefined,
        isCustomUpdate: undefined,
        unifiedResponseCode: undefined,
        customPrice: undefined,
    }));
    return easyPricingAdapter.upsertMany(updates, state);
}), on(EASY_PRICING_ACTIONS.resetProcessingStateForProduct, (state, action) => {
    const existingEasyPricing = state.entities[action.productNumber];
    if (existingEasyPricing) {
        return easyPricingAdapter.upsertOne({
            ...existingEasyPricing,
            processingState: undefined,
            isManualUpdate: undefined,
            isCustomUpdate: undefined,
            unifiedResponseCode: undefined,
            customPrice: undefined,
        }, state);
    }
    return state;
}));
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function easyPricingReducer(state, action) {
    return _easyPricingReducer(state, action);
}

const selectEasyPricingState = createFeatureSelector(EASY_PRICING_KEY);
/**
 * @param productNumbers
 * @returns true if all the product numbers are loaded, not found, reloading or error
 */
const isEasyPricingLoaded = (productNumbers) => createSelector(selectEasyPricingState, (easyPricingState) => productNumbers.every(productNumber => {
    const product = easyPricingState.entities[productNumber];
    return (product &&
        (product.loadingState === LoadingState.notFound ||
            product.loadingState === LoadingState.loaded ||
            product.loadingState === LoadingState.error ||
            product.loadingState === LoadingState.reloading));
}));
/**
 * @param productNumbers
 * @returns true if any of the product numbers are currently being processed
 */
const isAnyEasyPricingProcessing = (productNumbers) => createSelector(selectEasyPricingState, (easyPricingState) => productNumbers.some(productNumber => {
    const product = easyPricingState.entities[productNumber];
    return product?.processingState === ProcessingState.processing;
}));
const { selectIds: selectEasyPricingIds, selectEntities: selectEasyPricingEntities, selectAll: selectAllEasyPricing, selectTotal: selectTotalEasyPricing, } = easyPricingAdapter.getSelectors(selectEasyPricingState);

class EasyPricingService {
    constructor(store) {
        this.store = store;
        this.easyPricingState$ = this.store.select(selectEasyPricingState);
        this.easyPricingIds$ = this.store.select(selectEasyPricingIds);
        this.easyPricingEntities$ = this.store.select(selectEasyPricingEntities);
        this.isEasyPricingLoaded$ = (productNumbers) => this.store.select(isEasyPricingLoaded(productNumbers));
        this.isAnyEasyPricingProcessing$ = (productNumbers) => this.store.select(isAnyEasyPricingProcessing(productNumbers));
    }
    loadEasyPricing(productNumbers) {
        this.store.dispatch(EASY_PRICING_ACTIONS.loadEasyPricing({ productNumbers }));
    }
    updateEasyPricing(update, tandemSystem) {
        const isManual = update.isManualUpdate;
        const isCustom = update.isCustomUpdate;
        let tracking;
        if (isManual && update.manualUpdateReason) {
            // Manual approval tracing - requested price, reason
            tracking = traceHelperUtil.createManualApprovalStartTrackingObject(TRACE_CONTEXT.cpr, update.productNumber, update.price, update.manualUpdateReason);
        }
        else {
            // Auto approval tracing - requested price, product, target/custom price chosen
            tracking = traceHelperUtil.createAutoApprovalStartTrackingObject(TRACE_CONTEXT.cpr, update.productNumber, update.price, isCustom ? 'custom' : 'target');
        }
        this.store.dispatch(EASY_PRICING_ACTIONS.updateEasyPricing({
            easyPricingUpdate: update,
            tandemSystem,
            tracking,
        }));
    }
    resetProcessingStates() {
        this.store.dispatch(EASY_PRICING_ACTIONS.resetProcessingStates());
    }
    resetProcessingStateForProduct(productNumber) {
        this.store.dispatch(EASY_PRICING_ACTIONS.resetProcessingStateForProduct({ productNumber }));
    }
}
EasyPricingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EasyPricingService, deps: [{ token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
EasyPricingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EasyPricingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EasyPricingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }]; } });

var CprRequestTypeEnum;
(function (CprRequestTypeEnum) {
    CprRequestTypeEnum["minimum"] = "M";
    CprRequestTypeEnum["maximum"] = "X";
    CprRequestTypeEnum["target"] = "T";
    CprRequestTypeEnum["custom"] = "Y";
})(CprRequestTypeEnum || (CprRequestTypeEnum = {}));

const NGRX_PRICING_LIB_CONFIG = new InjectionToken('NGRX_PRICING_LIB_CONFIG');

const GLOBAL_PRICE_CACHE_CONTROL_FLAG_NAME = 'split_global_price_cache_control';

/* eslint-disable @typescript-eslint/no-shadow */
class PricingHttpClientService {
    constructor(config, http, platformService) {
        this.config = config;
        this.http = http;
        this.platformService = platformService;
    }
    get(path, watermark) {
        const url = watermark
            ? `${this.config.apiUrl}/${path}?watermark=${watermark}`
            : `${this.config.apiUrl}/${path}`;
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    getWithParams(path, params) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http
            .get(url, { params })
            .pipe(catchError(this.handleError));
    }
    getAsync(path, watermark, batchSize) {
        let url = `${this.config.apiUrl}/${path}?batchSize=${batchSize}`;
        if (watermark) {
            url += `&watermark=${watermark}`;
        }
        return this.http.get(url).pipe(catchError(this.handleError));
    }
    post(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http
            .post(url, body)
            .pipe(timeout(this.config.timeout ?? 30000), catchError(this.handleError));
    }
    postWithOptions(path, body) {
        const url = `${this.config.apiUrl}/${path}`;
        return this.http
            .post(url, body)
            .pipe(timeout(60000), catchError(this.handleError));
    }
    handleError(error) {
        console.error('5xW9LnV7Jg - NgRx Pricing - Http called failed', error);
        return throwError(error.message);
    }
}
PricingHttpClientService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingHttpClientService, deps: [{ token: NGRX_PRICING_LIB_CONFIG }, { token: i1$1.HttpClient }, { token: i2.PlatformService }], target: i0.ɵɵFactoryTarget.Injectable });
PricingHttpClientService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingHttpClientService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingHttpClientService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRICING_LIB_CONFIG]
                }] }, { type: i1$1.HttpClient }, { type: i2.PlatformService }]; } });

class ProductPricingEffects {
    constructor(actions$, httpService, store, panAppState, config) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.store = store;
        this.panAppState = panAppState;
        this.config = config;
        this.loadProductPricing$ = createEffect(() => this.actions$.pipe(ofType(PRODUCT_PRICING_ACTIONS.loadProductPricing), mergeMap(action => combineLatest([
            of(action),
            this.panAppState
                .feature$([GLOBAL_PRICE_CACHE_CONTROL_FLAG_NAME])
                .pipe(take(1)),
            this.store.select(selectAllProductPricing$1).pipe(take(1)),
        ])), mergeMap(([action, priceCacheControlSplitFlag, productPricings]) => {
            const productNumbersToFetch = this.filterOutProductNumbersWeAlreadyHavePricesFor(action.productNumbers, productPricings, priceCacheControlSplitFlag);
            return [
                PRODUCT_PRICING_ACTIONS.fetchProductPricing({
                    productNumbers: productNumbersToFetch,
                }),
            ];
        })));
        this.fetchProductPricing$ = createEffect(() => this.actions$.pipe(ofType(PRODUCT_PRICING_ACTIONS.fetchProductPricing), mergeMap(action => {
            const batchSize = this.config.priceBatchSize
                ? this.config.priceBatchSize
                : 1000; // items per chunk
            const productBatches = (!!action.productNumbers
                ? action.productNumbers
                : []).reduce((resultArray, item, index) => {
                const batchIndex = Math.floor(index / batchSize);
                if (!resultArray[batchIndex]) {
                    resultArray[batchIndex] = []; // start a new chunk
                }
                resultArray[batchIndex].push(item);
                return resultArray;
            }, []);
            return productBatches.map(batch => PRODUCT_PRICING_ACTIONS.fetchProductPricingBatch({
                productNumbers: batch,
                feature: getCurrentPathname(),
                watermark: action.watermark,
                tracking: priceTrackingEvents.initialPriceTrace(batch, true),
            }));
        })));
        this.fetchProductPricingBatch$ = createEffect(() => this.actions$.pipe(ofType(PRODUCT_PRICING_ACTIONS.fetchProductPricingBatch), mergeMap(action => {
            const params = new HttpParams().set('watermark', action.watermark ? action.watermark : null);
            return this.httpService
                .post('pricing', {
                productNumbers: action.productNumbers,
                feature: action.feature,
            })
                .pipe(switchMap(response => {
                if (!!response && !!response.messageDetail) {
                    return [
                        PRODUCT_PRICING_ACTIONS.fetchProductPricingSuccess({
                            requestedProductNumbers: action.productNumbers,
                            productPricing: response.messageDetail.productList,
                            effectiveDate: response.messageDetail.priceEffectiveDate,
                            effectiveTime: response.messageDetail.priceEffectiveTime,
                            tracking: priceTrackingEvents.finalPriceTrace(true, true),
                        }),
                    ];
                }
                else {
                    const error = new Error('Null response');
                    return [
                        PRODUCT_PRICING_ACTIONS.fetchProductPricingFail({
                            productNumbers: action.productNumbers,
                            error,
                            tracking: priceTrackingEvents.finalPriceTrace(false, true),
                        }),
                    ];
                }
            }), catchError((error) => of(PRODUCT_PRICING_ACTIONS.fetchProductPricingFail({
                productNumbers: action.productNumbers,
                error,
                tracking: priceTrackingEvents.finalPriceTrace(false, true),
            }))));
        })));
    }
    filterOutProductNumbersWeAlreadyHavePricesFor(productNumbers, productPricings, priceCacheControlSplitFlag) {
        const threshold = dayjs().subtract(this.config.pricingSeconds, 'seconds');
        const result = productNumbers.filter(productNumber => {
            // product number should not be undefined
            if (!productNumber) {
                return false;
            }
            const productPricing = productPricings.find(pricing => +pricing.productNumber === productNumber);
            if (!productPricing) {
                // Element wasn't found in the state, so we need to fetch it
                return true;
            }
            // Or element is in hasn't been fetch or previous attempt encountered an error
            const watermark = dayjs(productPricing?.watermark);
            return (productPricing.loadingState === LoadingState.init ||
                productPricing.loadingState === LoadingState.error ||
                productPricing.noCache === true ||
                (priceCacheControlSplitFlag && watermark.isBefore(threshold)));
        });
        return result;
    }
}
ProductPricingEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductPricingEffects, deps: [{ token: i1$2.Actions }, { token: PricingHttpClientService }, { token: i1.Store }, { token: i2.PanAppState }, { token: NGRX_PRICING_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
ProductPricingEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductPricingEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: ProductPricingEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: PricingHttpClientService }, { type: i1.Store }, { type: i2.PanAppState }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRICING_LIB_CONFIG]
                }] }]; } });

const NG_RX_PRICING_EVENTS = {
    namespace: 'Price',
    eventType: ['ProductPriceEvent'],
};
const SELLER_PRICING_EVENTS = {
    namespace: 'Price',
    eventType: ['SellerPriceDetailsEvent'],
};

class PricingClientWebSocketService {
    constructor(webSocketService, store) {
        this.webSocketService = webSocketService;
        this.store = store;
        this.subscriptions = [];
        /**
         * @remarks
         * This method maps the incoming websocket event action defined in @usf/list-types
         * to the appropriate NgRx action.  New events can be added to the map without impacting performance
         *
         * @param eventAction - The action attached to the websocket event
         * @param eventData - The data attached to the websocket event
         * @returns The NgRx action that will interact with the store
         */
        this.handleEvent = (eventAction, eventData) => ({
            ['GetPrices']: PRODUCT_PRICING_ACTIONS.productPriceCreated({
                productPricing: eventData.data.messageContent[0],
            }),
            [PriceEventActions.PriceUpdated]: PRODUCT_PRICING_ACTIONS.productPricingUpdated({ productPricing: eventData.data.messageContent[0] }),
            [PriceEventActions.PriceDeleted]: PRODUCT_PRICING_ACTIONS.productPricingDeleted({
                productPricingId: eventData.data.messageContent[0]?.productPricingId,
            }),
            // Seller Price
            [SellerPriceDetailsEventActions.GetSellerPriceDetails]: SELLER_PRICING_ACTIONS.sellerPriceCreated({
                sellerPricing: eventData.data.messageContent[0],
            }),
            [SellerPriceDetailsEventActions.SellerPriceDetailUpdated]: SELLER_PRICING_ACTIONS.sellerPricingUpdated({
                sellerPricing: eventData.data.messageContent[0],
            }),
            [SellerPriceDetailsEventActions.SellerPriceDetailDeleted]: SELLER_PRICING_ACTIONS.sellerPricingDeleted({
                productPricingId: eventData.data.messageContent[0]?.productPricingId,
            }),
        }[eventAction]);
    }
    initialize() {
        // if there is no websocket service injected, return out
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!this.webSocketService.baseUrl) {
            // eslint-disable-next-line no-console
            console.info('%c** no websocket injected for pricing domain **', 'color: black; font-weight:bold;');
            return;
        }
        // subscribe to the websocket events scoped to the list domain
        // panamax websocket service lib will handle keeping the websocket connection
        // alive and will update the isConnected$ observable accordingly
        this.subscriptions.push(this.webSocketService?.isConnected$.subscribe(connected => {
            if (connected) {
                // Register websocket namespace and event types to subscribe to
                this.webSocketService.register([
                    NG_RX_PRICING_EVENTS,
                    SELLER_PRICING_EVENTS,
                ]);
                this.subscriptions.push(this.webSocketService?.getTopicData().subscribe((event) => {
                    let eventData;
                    try {
                        if (this.isObject(JSON.parse(event?.data))) {
                            eventData = JSON.parse(event?.data);
                            // dispatch appropriate NgRx action based on incoming websocket event action
                            if (eventData?.data?.namespace === 'List' &&
                                eventData?.data?.eventAction) {
                                this.store.dispatch(this.handleEvent(eventData?.data?.eventAction, eventData));
                            }
                        }
                        else {
                            eventData = null;
                        }
                    }
                    catch (e) { }
                }));
            }
            else {
                // eslint-disable-next-line no-console
                console.info('%c** pricing domain web socket disconnected. **', 'color: red; font-weight:bold;');
            }
        }));
    }
    isObject(obj) {
        return obj === Object(obj);
    }
}
PricingClientWebSocketService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingClientWebSocketService, deps: [{ token: i1$3.WebSocketService }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
PricingClientWebSocketService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingClientWebSocketService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingClientWebSocketService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$3.WebSocketService }, { type: i1.Store }]; } });

class SellerPricingEffects {
    constructor(actions$, httpService) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.loadSellerPricing$ = createEffect(() => this.actions$.pipe(ofType(SELLER_PRICING_ACTIONS.loadSellerPricing), switchMap(action => {
            const params = new HttpParams().set('watermark', action.watermark ? action.watermark : null);
            return this.httpService
                .post('seller-pricing/v2', {
                productNumbers: action.productNumbers,
                feature: action.feature,
            })
                .pipe(map(response => {
                if (!!response && !!response.messageDetail) {
                    return SELLER_PRICING_ACTIONS.loadSellerPricingSuccess({
                        customerAscInd: response.messageDetail.customerAscInd,
                        sellerPriceDetails: response.messageDetail.productList,
                        effectiveDate: response.messageDetail.lastPricingDate,
                        effectiveTime: response.messageDetail.lastPricingTime,
                        districtNgpCprInd: response.messageDetail.districtNgpCprInd,
                        tracking: sellerPriceTrackingEvents.finalSellerPriceTrace(true),
                    });
                }
                else {
                    const error = new Error('null response');
                    SELLER_PRICING_ACTIONS.loadSellerPricingFail({
                        error,
                        tracking: sellerPriceTrackingEvents.finalSellerPriceTrace(false),
                    });
                }
            }), catchError((error) => of(SELLER_PRICING_ACTIONS.loadSellerPricingFail({
                error,
                tracking: sellerPriceTrackingEvents.finalSellerPriceTrace(false),
            }))));
        })));
    }
}
SellerPricingEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerPricingEffects, deps: [{ token: i1$2.Actions }, { token: PricingHttpClientService }], target: i0.ɵɵFactoryTarget.Injectable });
SellerPricingEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerPricingEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: SellerPricingEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: PricingHttpClientService }]; } });

class PricingStateService {
    constructor(store, panAppState) {
        this.store = store;
        this.panAppState = panAppState;
    }
    initialize() {
        this.panAppState.customer$
            .pipe(filter(customer => customer?.selected))
            .subscribe(customer => {
            this.store.dispatch(PRODUCT_PRICING_ACTIONS.resetStore());
            this.store.dispatch(SELLER_PRICING_ACTIONS.resetStore());
            this.store.dispatch(EASY_PRICING_ACTIONS.resetStore());
            // TODO INITIALIZE STORAGE
            // this.store.dispatch(ACTIONS.initializeStorage());
        });
    }
}
PricingStateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingStateService, deps: [{ token: i1.Store }, { token: i2.PanAppState }], target: i0.ɵɵFactoryTarget.Injectable });
PricingStateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingStateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: PricingStateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Store }, { type: i2.PanAppState }]; } });

var CprRequestActionEnum;
(function (CprRequestActionEnum) {
    /**
     * Legacy option
     */
    CprRequestActionEnum["none"] = "";
    /**
     * Zilliant manual
     */
    CprRequestActionEnum["manual"] = "A";
    /**
     * Zilliant Auto
     */
    CprRequestActionEnum["auto"] = "G";
})(CprRequestActionEnum || (CprRequestActionEnum = {}));

const cprResponseCodes = {
    operationSuccess: 0,
    zilliantAutomaticSuccess: 200,
    zilliantManualSuccess: 201,
    zilliantManualOperationRequired: 300,
    generalError: 500,
};

class CprPricingEffects {
    constructor(actions$, httpService, panAppState, store) {
        this.actions$ = actions$;
        this.httpService = httpService;
        this.panAppState = panAppState;
        this.store = store;
        this.updateCprPricing$ = createEffect(() => this.actions$.pipe(ofType(CPR_PRICING_ACTIONS.updateCPRPricing), concatLatestFrom(action => this.getCprUpdateRequest(action.productNumber, action.targetPrice, action.requestType, action.districtNgpCprInd, action.productUOM, action.requestManualApproval, action.priceEffectiveDate, action.tandemSystem, action.reason)), mergeMap(([action, request]) => this.httpService
            .postWithOptions('cpr-update', request)
            .pipe(switchMap(response => {
            // TODO HANDLE RESPONSE
            const productNumber = response?.customerList[0]?.productList[0]?.productNumber;
            const unifiedResponseCode = this.unifyResponseCode(response);
            if (this.isSuccessfulOperation(unifiedResponseCode)) {
                return [
                    CPR_PRICING_ACTIONS.updateCPRPricingSuccess({
                        result: response,
                        tracking: traceHelperUtil.endTrace(TRACE_CONTEXT.cpr, action.productNumber, action.targetPrice),
                        unifiedResponseCode,
                    }),
                    PRODUCT_PRICING_ACTIONS.fetchProductPricing({
                        productNumbers: [productNumber],
                        tracking: priceTrackingEvents.initialPriceTrace([
                            productNumber,
                        ]),
                    }),
                    SELLER_PRICING_ACTIONS.loadSellerPricing({
                        productNumbers: [productNumber],
                        feature: getCurrentPathname(),
                        tracking: sellerPriceTrackingEvents.initialSellerPriceTrace([productNumber]),
                    }),
                ];
            }
            else {
                const error = new Error('Null response');
                return [
                    CPR_PRICING_ACTIONS.updateCPRPricingFail({
                        result: response,
                        error,
                        tracking: traceHelperUtil.endTrace(TRACE_CONTEXT.cpr, action.productNumber, action.targetPrice),
                        unifiedResponseCode,
                    }),
                ];
            }
        }), catchError((error) => of(CPR_PRICING_ACTIONS.updateCPRPricingFail({
            result: request,
            error,
            tracking: traceHelperUtil.endTrace(TRACE_CONTEXT.cpr, action.productNumber, action.targetPrice),
            unifiedResponseCode: this.unifyResponseCode(error),
        })))))));
        this.unifyResponseCode = (response) => {
            if (!response) {
                return cprResponseCodes.generalError;
            }
            if (response.hasOwnProperty('responseCode') && 'responseCode' in response) {
                // handle CprResponse logic
                const responseCode = +response.responseCode;
                if (responseCode >= 0 &&
                    response.customerList?.length > 0 &&
                    response.customerList[0]?.productList?.length > 0) {
                    const productResult = response.customerList[0]?.productList[0];
                    return +(productResult?.returnCode ?? cprResponseCodes.generalError);
                }
                else {
                    return cprResponseCodes.generalError;
                }
            }
            if (response.hasOwnProperty('status') && 'status' in response) {
                // handle HttpErrorResponse logic
                return response.status;
            }
            // handle other response types
            return cprResponseCodes.generalError;
        };
        this.isSuccessfulOperation = (unifiedResponseCode) => unifiedResponseCode >= 0 && unifiedResponseCode < 400;
        this.getCprUpdateRequest = (productNumber, targetPrice, requestType, districtNgpCprInd, productUOM, requestManualApproval, priceEffectiveDate, tandemSystem, reason) => this.panAppState.user$.pipe(take(1), concatLatestFrom(() => this.panAppState.customer$), take(1), switchMap(([user, customer]) => of(this.buildCprUpdateRequest(productNumber, targetPrice, requestType, districtNgpCprInd, productUOM, requestManualApproval, priceEffectiveDate, user?.userId, customer?.divisionNumber, customer?.customerNumber, tandemSystem, reason))));
        this.buildCprUpdateRequest = (productNumber, targetPrice, requestType, districtNgpCprInd, productUOM, requestManualApproval, priceEffectiveDate, userId, divisionNumber, customerNumber, tandemSystem, reason) => {
            const cprRequestProductList = {
                productNumber,
                priceUom: productUOM,
                requestedPrice: targetPrice,
                requestedType: requestType,
                requestedAction: this.getProductRequestAction(districtNgpCprInd, requestManualApproval),
                reason,
            };
            const cprRequestCustomerList = {
                customerNumber,
                productList: [cprRequestProductList],
            };
            const request = {
                divisionNumber,
                requestorId: userId,
                timeZone: this.getTimeZone(districtNgpCprInd),
                priceEffectiveDateTime: priceEffectiveDate,
                cprSource: 'EC',
                districtNgpCprInd,
                customerList: [cprRequestCustomerList],
                tandemSystem,
            };
            return request;
        };
        this.getProductRequestAction = (districtNgpCprInd, requestManualApproval) => {
            if (this.isLegacy(districtNgpCprInd)) {
                return CprRequestActionEnum.none;
            }
            return requestManualApproval
                ? CprRequestActionEnum.manual
                : CprRequestActionEnum.auto;
        };
        this.getTimeZone = (districtNgpCprInd) => this.isLegacy(districtNgpCprInd)
            ? null
            : Intl.DateTimeFormat().resolvedOptions().timeZone;
        this.isLegacy = (districtNgpCprInd) => 'Y' !== districtNgpCprInd;
    }
}
CprPricingEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CprPricingEffects, deps: [{ token: i1$2.Actions }, { token: PricingHttpClientService }, { token: i2.PanAppState }, { token: i1.Store }], target: i0.ɵɵFactoryTarget.Injectable });
CprPricingEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CprPricingEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: CprPricingEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: PricingHttpClientService }, { type: i2.PanAppState }, { type: i1.Store }]; } });

const getFormattedPriceEffectiveDate = (date) => {
    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    const msLocal = date.getTime() - offsetMs;
    const dateLocal = new Date(msLocal);
    return dateLocal.toISOString();
};
const isSuccessfulOperation = (unifiedResponseCode) => unifiedResponseCode >= 0 && unifiedResponseCode < 400;
const unifyResponseCode = (response) => {
    if (!response) {
        return cprResponseCodes.generalError;
    }
    if (response.hasOwnProperty('responseCode') && 'responseCode' in response) {
        // handle CprResponse logic
        const responseCode = +response.responseCode;
        if (responseCode >= 0 &&
            response.customerList?.length > 0 &&
            response.customerList[0]?.productList?.length > 0) {
            const productResult = response.customerList[0]?.productList[0];
            return +(productResult?.returnCode ?? cprResponseCodes.generalError);
        }
        else {
            return cprResponseCodes.generalError;
        }
    }
    if (response.hasOwnProperty('status') && 'status' in response) {
        // handle HttpErrorResponse logic
        return response.status;
    }
    // handle other response types
    return cprResponseCodes.generalError;
};

/* eslint-disable max-len */
/* eslint-disable no-shadow */
var AnalyticMessages;
(function (AnalyticMessages) {
    // Capabilities
    AnalyticMessages["capability"] = "lists";
    // Events
    AnalyticMessages["customPricingEvent"] = "custom pricing rationale submit";
    // Miscellaneous
    AnalyticMessages["customPricingType"] = "custom";
    AnalyticMessages["targetPricingType"] = "target";
    AnalyticMessages["customManualPricingType"] = "custom manual approval";
    AnalyticMessages["priceApprovalRequest"] = "price approval request";
})(AnalyticMessages || (AnalyticMessages = {}));

const updateEasyPricingAnalyticsTransformer = (tracking) => ({
    event: tracking.data.event,
    capability: tracking.data.capability,
    products: [
        {
            divisionApn: `${tracking.data.product.divisionNumber}-${tracking.data.product.productNumber}`,
            attributes: tracking.data.product.trackingAttributes,
            cpr: '1',
            setPriceType: tracking.data.product.isManualUpdate
                ? AnalyticMessages.customManualPricingType
                : tracking.data.product.isCustomUpdate
                    ? AnalyticMessages.customPricingType
                    : AnalyticMessages.targetPricingType,
            casePrice: tracking.data.product.price,
        },
    ],
    ...(tracking.data.product.isManualUpdate && {
        file: {
            type: `${AnalyticMessages.priceApprovalRequest}: ${tracking.data.product.manualUpdateReason.toLowerCase()}`,
        },
    }),
});

class EasyPricingEffects {
    constructor(actions$, easyPricingService, httpService, panAppState, config) {
        this.actions$ = actions$;
        this.easyPricingService = easyPricingService;
        this.httpService = httpService;
        this.panAppState = panAppState;
        this.config = config;
        this.loadEasyPricing$ = createEffect(() => this.actions$.pipe(ofType(EASY_PRICING_ACTIONS.loadEasyPricing), withLatestFrom(this.easyPricingService.easyPricingEntities$), map(([action, easyPricingEntities]) => {
            const productNumbersToFetch = this.filterOutProductNumbersWeAlreadyHavePricesFor(action.productNumbers, easyPricingEntities, action.fridayPricing, action.overrideCache);
            return EASY_PRICING_ACTIONS.fetchEasyPricing({
                productNumbers: productNumbersToFetch,
                feature: action.feature,
                fridayPricing: action.fridayPricing,
                isFromFridayToggle: action.isFromFridayToggle,
            });
        })));
        this.fetchEasyPricing$ = createEffect(() => this.actions$.pipe(ofType(EASY_PRICING_ACTIONS.fetchEasyPricing), filter(action => action.productNumbers.length > 0), mergeMap(action => {
            const pmeRequestBody = {
                productNumbers: action.productNumbers,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
            // Only add optional properties if they are defined
            if (action.feature !== undefined) {
                pmeRequestBody.feature = action.feature;
            }
            if (action.fridayPricing !== undefined) {
                pmeRequestBody.fridayPricing = action.fridayPricing;
            }
            return this.httpService
                .post('seller-pricing-pme', pmeRequestBody)
                .pipe(switchMap(response => {
                if (!!response && !!response.messageDetail) {
                    return [
                        EASY_PRICING_ACTIONS.fetchEasyPricingSuccess({
                            productList: response.messageDetail.productList,
                            requestedProductNumbers: action.productNumbers,
                            isFridayPricing: action.fridayPricing ?? false,
                            isFromFridayToggle: action.isFromFridayToggle ?? false,
                        }),
                    ];
                }
                else {
                    const error = new Error('Null response');
                    return [
                        EASY_PRICING_ACTIONS.fetchEasyPricingFail({
                            productNumbers: action.productNumbers,
                            isFridayPricing: action.fridayPricing ?? false,
                            isFromFridayToggle: action.isFromFridayToggle ?? false,
                            error,
                        }),
                    ];
                }
            }), catchError((error) => of(EASY_PRICING_ACTIONS.fetchEasyPricingFail({
                productNumbers: action.productNumbers,
                isFridayPricing: action.fridayPricing ?? false,
                isFromFridayToggle: action.isFromFridayToggle ?? false,
                error,
            }))));
        })));
        this.updateEasyPricing$ = createEffect(() => this.actions$.pipe(ofType(EASY_PRICING_ACTIONS.updateEasyPricing), withLatestFrom(this.easyPricingService.easyPricingEntities$, this.panAppState.customer$, this.panAppState.user$), mergeMap(([action, easyPricingEntities, customer, user]) => {
            const cprRequest = this.getCprRequest(action.easyPricingUpdate, action.tandemSystem, easyPricingEntities, customer, user);
            let url = 'cpr-update';
            if (action.easyPricingUpdate.isFridayPricing) {
                url += '?fridayPricing=true';
            }
            return this.httpService
                .postWithOptions(url, cprRequest)
                .pipe(concatMap(response => {
                const unifiedResponseCode = unifyResponseCode(response);
                const productNumber = action.easyPricingUpdate.productNumber;
                const responseProduct = response?.customerList?.[0]?.productList?.[0];
                const responseMessage = responseProduct?.returnText ?? 'Null response';
                if (isSuccessfulOperation(unifiedResponseCode)) {
                    return this.getCprUpdateSuccessActions(action.easyPricingUpdate, response, unifiedResponseCode, action.tracking, responseMessage);
                }
                else {
                    return [
                        EASY_PRICING_ACTIONS.updateEasyPricingFail({
                            unifiedResponseCode,
                            easyPricingUpdate: action.easyPricingUpdate,
                            error: new Error('Null response'),
                            tracking: traceHelperUtil.createCprFailureTrackingObject(action.tracking, new Error(responseMessage), productNumber, unifiedResponseCode),
                        }),
                    ];
                }
            }), catchError((error) => {
                const productNumber = action.easyPricingUpdate.productNumber;
                return of(EASY_PRICING_ACTIONS.updateEasyPricingFail({
                    easyPricingUpdate: action.easyPricingUpdate,
                    unifiedResponseCode: error.status,
                    error,
                    tracking: traceHelperUtil.createCprFailureTrackingObject(action.tracking, error, productNumber),
                }));
            }));
        })));
    }
    filterOutProductNumbersWeAlreadyHavePricesFor(productNumbers, easyPricingEntities, isFridayPricing, overrideCache) {
        const threshold = dayjs().subtract(this.config.pricingSeconds, 'seconds');
        const result = productNumbers.filter(productNumber => {
            // product number should not be undefined
            if (!productNumber) {
                return false;
            }
            const easyPricing = easyPricingEntities[productNumber];
            if (!easyPricing) {
                // Element wasn't found in the state, so we need to fetch it
                return true;
            }
            // Or element is in hasn't been fetch or previous attempt encountered an error
            const watermark = dayjs(easyPricing?.watermark);
            return (easyPricing.loadingState === LoadingState.error ||
                watermark.isBefore(threshold) ||
                easyPricing.isFridayPricing !== isFridayPricing ||
                overrideCache);
        });
        return result;
    }
    getUpdatedPrice(easyPricingUpdate, easyPricingState) {
        if (easyPricingUpdate.isCustomUpdate &&
            easyPricingState.currentPrice.autoShippingCharges?.ascApplyFlag === 'P') {
            return (easyPricingUpdate.price -
                (easyPricingState.currentPrice.autoShippingCharges.unitAscRate ?? 0));
        }
        else {
            return easyPricingUpdate.price;
        }
    }
    getCprRequest(easyPricingUpdate, tandemSystem, easyPricingEntities, customer, user) {
        const easyPricingState = easyPricingEntities[easyPricingUpdate.productNumber];
        const updatedPrice = this.getUpdatedPrice(easyPricingUpdate, easyPricingState);
        const requestedType = easyPricingUpdate.isCustomUpdate
            ? CprRequestTypeEnum.custom
            : CprRequestTypeEnum.target;
        const requestedAction = easyPricingUpdate.isManualUpdate
            ? CprRequestActionEnum.manual
            : CprRequestActionEnum.auto;
        // Will change once we implement friday pricing
        const priceEffectiveDateTime = getFormattedPriceEffectiveDate(new Date());
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const cprRequestProductList = {
            productNumber: easyPricingUpdate.productNumber,
            priceUom: easyPricingUpdate.productUOM,
            reason: easyPricingUpdate.manualUpdateReason
                ? easyPricingUpdate.manualUpdateReason
                : undefined,
            requestedPrice: updatedPrice,
            requestedType,
            requestedAction,
        };
        const cprRequestCustomerList = {
            customerNumber: customer?.customerNumber,
            productList: [cprRequestProductList],
        };
        const request = {
            divisionNumber: customer?.divisionNumber,
            requestorId: user?.userId,
            timeZone,
            priceEffectiveDateTime,
            cprSource: 'EC',
            districtNgpCprInd: 'Y',
            customerList: [cprRequestCustomerList],
            tandemSystem,
        };
        return request;
    }
    getCprUpdateSuccessActions(easyPricingUpdate, result, unifiedResponseCode, originalTracking, responseMessage) {
        const successTracking = traceHelperUtil.createCprSuccessTrackingObject(originalTracking, unifiedResponseCode, responseMessage, easyPricingUpdate.productNumber);
        const analytics = {
            analytics: {
                data: {
                    capability: AnalyticMessages.capability,
                    event: AnalyticMessages.customPricingEvent,
                    product: {
                        ...easyPricingUpdate,
                        divisionNumber: result.divisionNumber,
                        unifiedResponseCode,
                    },
                },
                transformFunc: updateEasyPricingAnalyticsTransformer,
            },
        };
        return [
            EASY_PRICING_ACTIONS.updateEasyPricingSuccess({
                result,
                easyPricingUpdate,
                tracking: {
                    ...successTracking,
                    ...(unifiedResponseCode !== 300 ? analytics : {}),
                },
                unifiedResponseCode,
            }),
            PRODUCT_PRICING_ACTIONS.fetchProductPricing({
                productNumbers: [easyPricingUpdate.productNumber],
                tracking: priceTrackingEvents.initialPriceTrace([
                    easyPricingUpdate.productNumber,
                ]),
            }),
            EASY_PRICING_ACTIONS.loadEasyPricing({
                productNumbers: [easyPricingUpdate.productNumber],
                fridayPricing: easyPricingUpdate.isFridayPricing,
                isFromFridayToggle: false,
                overrideCache: true,
            }),
        ];
    }
}
EasyPricingEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EasyPricingEffects, deps: [{ token: i1$2.Actions }, { token: EasyPricingService }, { token: PricingHttpClientService }, { token: i2.PanAppState }, { token: NGRX_PRICING_LIB_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
EasyPricingEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EasyPricingEffects, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: EasyPricingEffects, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1$2.Actions }, { type: EasyPricingService }, { type: PricingHttpClientService }, { type: i2.PanAppState }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRICING_LIB_CONFIG]
                }] }]; } });

class NgRxPricingModule {
    constructor(ngRxPricingLibConfig, store, panAppState, pricingStateService, webSocketService) {
        this.ngRxPricingLibConfig = ngRxPricingLibConfig;
        this.store = store;
        this.panAppState = panAppState;
        this.pricingStateService = pricingStateService;
        this.webSocketService = webSocketService;
    }
    static forRoot(ngRxPricingLibConfig, store, webSocketService) {
        return {
            ngModule: NgRxPricingModule,
            providers: [
                {
                    provide: NGRX_PRICING_LIB_CONFIG,
                    useValue: ngRxPricingLibConfig,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializeWebSocket,
                    deps: [PricingClientWebSocketService],
                    multi: true,
                },
                {
                    provide: APP_INITIALIZER,
                    useFactory: initializePricingState,
                    deps: [PricingStateService],
                    multi: true,
                },
            ],
        };
    }
}
NgRxPricingModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxPricingModule, deps: [{ token: NGRX_PRICING_LIB_CONFIG }, { token: i1.Store }, { token: i2.PanAppState }, { token: PricingStateService }, { token: i1$3.WebSocketService, optional: true }], target: i0.ɵɵFactoryTarget.NgModule });
NgRxPricingModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: NgRxPricingModule, imports: [i1.StoreFeatureModule, i1$2.EffectsFeatureModule, i1.StoreFeatureModule, i1$2.EffectsFeatureModule, i1.StoreFeatureModule, i1$2.EffectsFeatureModule, i1.StoreFeatureModule, i1$2.EffectsFeatureModule] });
NgRxPricingModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxPricingModule, providers: [
        PricingHttpClientService,
        ProductPricingEffects,
        SellerPricingEffects,
        SelectPricingService,
        CprPricingEffects,
        CprPricingService,
        EasyPricingEffects,
        EasyPricingService,
    ], imports: [StoreModule.forFeature(PRICING_KEY, productPricingReducer),
        EffectsModule.forFeature([ProductPricingEffects]),
        StoreModule.forFeature(SELLER_PRICING_KEY, sellerPricingReducer),
        EffectsModule.forFeature([SellerPricingEffects]),
        StoreModule.forFeature(CPR_PRICING_KEY, cprPricingReducer),
        EffectsModule.forFeature([CprPricingEffects]),
        StoreModule.forFeature(EASY_PRICING_KEY, easyPricingReducer),
        EffectsModule.forFeature([EasyPricingEffects])] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: NgRxPricingModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(PRICING_KEY, productPricingReducer),
                        EffectsModule.forFeature([ProductPricingEffects]),
                        StoreModule.forFeature(SELLER_PRICING_KEY, sellerPricingReducer),
                        EffectsModule.forFeature([SellerPricingEffects]),
                        StoreModule.forFeature(CPR_PRICING_KEY, cprPricingReducer),
                        EffectsModule.forFeature([CprPricingEffects]),
                        StoreModule.forFeature(EASY_PRICING_KEY, easyPricingReducer),
                        EffectsModule.forFeature([EasyPricingEffects]),
                    ],
                    providers: [
                        PricingHttpClientService,
                        ProductPricingEffects,
                        SellerPricingEffects,
                        SelectPricingService,
                        CprPricingEffects,
                        CprPricingService,
                        EasyPricingEffects,
                        EasyPricingService,
                    ],
                    exports: [],
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [NGRX_PRICING_LIB_CONFIG]
                }] }, { type: i1.Store }, { type: i2.PanAppState }, { type: PricingStateService }, { type: i1$3.WebSocketService, decorators: [{
                    type: Optional
                }] }]; } });
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function initializeWebSocket(webSocketService) {
    const websocket = () => webSocketService.initialize();
    return websocket;
}
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function initializePricingState(pricingStateService) {
    const pricingState = () => pricingStateService.initialize();
    return pricingState;
}

/*
 * Public API Surface of ngrx-pricing
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CPR_PRICING_ACTIONS, CPR_PRICING_KEY, CprPricingService, CprRequestTypeEnum, EASY_PRICING_ACTIONS, EASY_PRICING_KEY, EasyPricingService, LoadingState, ManualUpdateReason, NgRxPricingModule, PRICING_KEY, PRODUCT_PRICING_ACTIONS, ProcessingState, ProductPricingService, SELLER_PRICING_ACTIONS, SELLER_PRICING_KEY, SelectPricingService, cprPricingAdapter, cprPricingReducer, easyPricingAdapter, easyPricingReducer, initialEasyPricingState, isEasyPricingLoaded, isProductPricingLoaded, productPricingAdapter, productPricingLoaded, productPricingReducer, selectAllCprPrice, selectAllSellerPrice, selectCprResponse, selectEasyPricingEntities, selectEasyPricingState, selectProductPricingEntities$1 as selectProductPricingEntities, selectProductPricingState, selectSellerPricingState, sellerPricingAdapter, sellerPricingReducer };
